import { policyClient } from '@api/sdk-client';
import { useClerk, useSignIn } from '@clerk/react';
import type { SessionResource } from '@clerk/react/types';
import { useCallback, useState } from 'react';
import type { ClerkMeAccount } from 'src/contexts/ClerkMeContext';

export type ClerkOrganizationMembership = {
  organization: {
    id: string;
    name: string;
    imageUrl?: string | null;
  };
};

/** `no-organization`: the Clerk user has zero memberships (e.g. a new self-service signup), not a failure. */
export type ClerkSessionFinalization = 'ready' | 'contact-support' | 'no-organization' | 'unavailable';

/**
 * `finalizeSessionWithOrganization` completes a sign-in the caller drives itself (password, email code).
 * `validateActiveSession` reuses a completed sign-in, including an existing pending
 * choose-organization session on return to /auth. It selects its sole membership and
 * checks Policy without creating or finalizing another sign-in.
 * `activateSession` selects an organization on a session and returns false if it is still pending.
 */
export function useClerkSessionFinalizer() {
  const { signIn } = useSignIn();
  const clerk = useClerk();
  const [finalization, setFinalization] = useState<ClerkSessionFinalization>('unavailable');

  const activateSession = useCallback(
    async (session: SessionResource, organization: string, existingSession?: SessionResource): Promise<boolean> => {
      await clerk.setActive({
        ...(existingSession ? { session: existingSession.id } : {}),
        organization,
      });
      // clerk-js posts the touch that persists the organization (and clears choose-organization) only while the
      // tab has focus, so setActive can resolve with the session still pending. Touch explicitly, then mint a
      // token for the activated session so Policy never sees the stale pending JWT.
      if (session.status !== 'pending') return true;
      await session.touch({ intent: 'select_org' });
      if (session.status === 'pending') return false;
      await session.getToken({ skipCache: true });
      return true;
    },
    [clerk],
  );

  const validateActiveSession = useCallback(
    async (existingSession?: SessionResource): Promise<ClerkSessionFinalization> => {
      const session = existingSession ?? clerk.session;
      if (!session?.id) return 'unavailable';
      const user = existingSession ? existingSession.user : clerk.user;
      // Reloaded user resources can omit memberships even after email-link verification succeeds.
      const page = await user?.getOrganizationMemberships();
      // A missing user or page is unknown, not zero: only a confirmed empty membership list is a new account.
      if (!page) {
        setFinalization('contact-support');
        return 'contact-support';
      }
      const memberships = page.data ?? [];
      const membershipCount = Math.max(page.total_count ?? 0, memberships.length);

      if (membershipCount === 0) {
        setFinalization('no-organization');
        return 'no-organization';
      }
      if (membershipCount === 1) {
        const activated = await activateSession(session, memberships[0].organization.id, existingSession);
        if (!activated) {
          setFinalization('contact-support');
          return 'contact-support';
        }
      } else if (existingSession?.status === 'pending' || !session.lastActiveOrganizationId) {
        setFinalization('contact-support');
        return 'contact-support';
      }

      const { data } = await policyClient.instance.get<{ activeAccount: ClerkMeAccount | null }>('/v1/me');
      if (!data.activeAccount) {
        setFinalization('contact-support');
        return 'contact-support';
      }

      setFinalization('ready');
      return 'ready';
    },
    [clerk, activateSession],
  );

  const finalizeSessionWithOrganization = useCallback(async (): Promise<ClerkSessionFinalization> => {
    if (!signIn) return 'unavailable';
    await signIn.finalize();
    return validateActiveSession();
  }, [signIn, validateActiveSession]);

  return { finalizeSessionWithOrganization, validateActiveSession, activateSession, finalization };
}
